import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { FaLaptopCode, FaBriefcase, FaCertificate, FaUser, FaTrophy, FaTimes, FaSpinner, FaLightbulb, FaImages } from 'react-icons/fa';

const AboutContainer = styled.div`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AboutContent = styled.div`
  line-height: 1.8;
  color: #2c3e50;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #2c3e50;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  &:after {
    content: '';
    display: block;
    width: 140px;
    height: 4px;
    background-color: #007bff;
    margin: 1rem auto;
    border-radius: 2px;
  }
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  }

  p {
    color: #4a5568;
    font-size: 1.05rem;
    margin-bottom: 1rem;
    line-height: 1.7;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #007bff;
    font-size: 1.4rem;
  }
`;

const SkillChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1rem;
`;

const SkillChip = styled.span`
  background: #eef4fc;
  color: #0056b3;
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid rgba(0, 123, 255, 0.15);
  transition: all 0.2s ease;

  &:hover {
    background: #007bff;
    color: white;
    transform: translateY(-2px);
  }
`;

const PhotoGallery = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  padding: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
`;

const Photo = styled.div`
  width: 100%;
  padding-top: 75%;
  background-color: #f0f0f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);

    &:before {
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }
`;

const FullScreenImage = styled.img`
  max-width: 95%;
  max-height: 95vh;
  object-fit: contain;
  border-radius: 8px;
  transform: scale(0.9);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1);
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1001;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const LoadingSpinner = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  z-index: 1001;
  animation: spin 1s linear infinite;
  
  svg {
    font-size: 3rem;
  }

  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  opacity: ${props => props.isLoaded ? 1 : 0};
  transition: opacity 0.3s ease;
`;

function About() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    if (selectedImage) {
      setIsImageLoading(true);
      setIsImageLoaded(false);
    }
  }, [selectedImage]);

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setIsImageLoaded(true);
  };

  const images = [
    '/assets/about-images/1.jpg',
    '/assets/about-images/2.jpg',
    '/assets/about-images/3.jpg',
    '/assets/about-images/4.jpg',
    '/assets/about-images/5.jpg',
    '/assets/about-images/6.jpg',
    '/assets/about-images/7.jpg',
    '/assets/about-images/8.png',
    '/assets/about-images/9.jpg',
    '/assets/about-images/10.jpg',
    '/assets/about-images/11.jpg',
    '/assets/about-images/12.jpg'
  ];

  return (
    <AboutContainer>
      <Title>About Me</Title>
      <AboutContent>
        <Section>
          <SectionTitle>
            <FaUser />
            Introduction
          </SectionTitle>
          <p>
            Hello! I am Nimish Kushwaha, a passionate Full Stack Developer currently pursuing my B.Tech in Computer Science and Engineering at Amrita Vishwa Vidyapeetham, Chennai.
          </p>
        </Section>

        <Section>
          <SectionTitle>
            <FaLaptopCode />
            Technical Expertise
          </SectionTitle>
          <p>
            I have experience across modern technologies, frameworks, and programming languages:
          </p>
          <SkillChips>
            <SkillChip>React.js</SkillChip>
            <SkillChip>Node.js</SkillChip>
            <SkillChip>Flutter</SkillChip>
            <SkillChip>MongoDB</SkillChip>
            <SkillChip>MySQL</SkillChip>
            <SkillChip>JavaScript</SkillChip>
            <SkillChip>Python</SkillChip>
            <SkillChip>Dart</SkillChip>
            <SkillChip>C / C++</SkillChip>
            <SkillChip>Git & GitHub</SkillChip>
            <SkillChip>Firebase</SkillChip>
          </SkillChips>
        </Section>

        <Section>
          <SectionTitle>
            <FaBriefcase />
            Professional Experience
          </SectionTitle>
          <p>
            My professional experience includes an internship as a Full Stack Developer at Corizo, where I designed and deployed scalable applications, enhanced data integrity through MongoDB schema redesign, and improved team efficiency using Git and GitHub.
          </p>
        </Section>

        <Section>
          <SectionTitle>
            <FaCertificate />
            Certifications & Leadership
          </SectionTitle>
          <p>
            I hold certifications in Full Stack Development from Corizo and Supervised Machine Learning from Stanford University. As the Lead Developer & Technical Coordinator of the Mahi-Eco Club, I have led significant initiatives such as the "Green Hackathon" and improved user engagement for web applications.
          </p>
        </Section>

        <Section>
          <SectionTitle>
            <FaLightbulb />
            Personal Drive
          </SectionTitle>
          <p>
            I am driven by my love for coding, problem-solving, and making a meaningful impact through technology. When I am not working on projects, I enjoy exploring new tools, contributing to open-source communities, and staying updated with the latest trends in technology.
          </p>
        </Section>

        <Section>
          <SectionTitle>
            <FaTrophy />
            Achievements
          </SectionTitle>
          <p>
            In 2024, I participated in the Smart India Hackathon Software Edition, where my team was selected for the Grand Finale. We developed innovative solutions to enhance rural education, showcasing real-time collaboration and impactful problem-solving.
          </p>
        </Section>

        <Section>
          <SectionTitle>
            <FaImages />
            Gallery
          </SectionTitle>
          <PhotoGallery>
            {images.map((image, index) => (
              <Photo
                key={index}
                style={{ backgroundImage: `url(${image})` }}
                onClick={() => setSelectedImage(image)}
              />
            ))}
          </PhotoGallery>
        </Section>
      </AboutContent>

      {selectedImage && (
        <Modal onClick={() => setSelectedImage(null)}>
          <CloseButton onClick={() => setSelectedImage(null)}>
            <FaTimes />
          </CloseButton>
          {isImageLoading && (
            <LoadingSpinner>
              <FaSpinner />
            </LoadingSpinner>
          )}
          <ImageContainer isLoaded={isImageLoaded}>
            <FullScreenImage 
              src={selectedImage} 
              alt="Full screen view" 
              onClick={(e) => e.stopPropagation()}
              onLoad={handleImageLoad}
            />
          </ImageContainer>
        </Modal>
      )}
    </AboutContainer>
  );
}

export default About;
